/*
 * FYS-5E Part 2 — Find Your Sound banner homepage player.
 *
 * Loaded by sections/wsh-fys-banner.liquid ONLY when `mode == homepage`.
 * Three product rows that each carry a preview MP3 (data-wsh-fys-track);
 * audio source = `product.metafields.tracks.demo | file_url` — same
 * metafield the PDP play-pill and the app-extension's `<wsh-audio-manager>`
 * read. Waveform renders inline via WaveSurfer (same lib the audio-manager
 * uses, exposed at `window.WaveSurfer`); when unavailable the banner falls
 * back to the 2px linear progress bar painted in CSS.
 *
 * Round 3 §1 (Phil 2026-10-14):
 *   §1a Pause-contract self-loop fix. The banner USED to dispatch pause
 *       events on local play AND listen for the same events, so a NEXT
 *       or row-click paused itself (flicker, no sound). Fix: tag each
 *       outbound CustomEvent with `detail.source = BANNER_SIG` (unique
 *       per banner-root) and ignore inbound events whose detail.source
 *       matches. The 60ms suppress window is retained as a belt-and-
 *       braces guard against event-loop reordering between browsers.
 *   §1b Duration: compute real audio duration from the `.mp3.json`
 *       sidecar (`length × samples_per_pixel / sample_rate`) when it
 *       is present, else fall back to WaveSurfer's own decoded value.
 *       Never derive duration from `data.length` — that was the
 *       off-by-20× bug that showed 30:18 for a 1:30 track.
 *   §1c Waveform-on-load. Row 0 fetches peaks + loads on init so the
 *       waveform paints before any press. selectRow / nextTrack use a
 *       playOnReady flag to defer the actual `.play()` until the new
 *       track emits `ready`, which is when audio can start reliably.
 *   §1d Hero "Play Real Sounds" wiring switched from `#play-first-demo`
 *       (which the old wsh-collection-tabs handler also listens to via
 *       href match — plays the first product in "Find your perfect
 *       sound" + opens the soundbar) to `#find-your-sound`. The old
 *       handler is href-bound so swapping the href defuses it. The
 *       inline anchor target in the banner markup moves to match.
 */
(function () {
  if (window.__wshFysBannerBound) return;
  window.__wshFysBannerBound = true;

  // CL-5 (2026-10-28): the same player also runs inside the /collections/all
  // router panels (snippets/wsh-fys-player.liquid, router variant). Router
  // roots carry data-wsh-fys-player-root + data-wsh-fys-lazy and start only
  // when their panel first opens (a 'wsh-fys-player:activate' event on the
  // root), so a closed panel never paints a zero-width waveform or fetches
  // audio at page load. Homepage behaviour is unchanged.
  const HOME_SELECTOR = '[data-wsh-fys-banner="homepage"]';
  const SELECTOR = HOME_SELECTOR + ', [data-wsh-fys-player-root]';
  const PAUSE_EVENTS_OUT = ['product:audio:pause', 'soundbar:pause', 'videos:pause', 'wsh-fys:audio:pause'];
  const PAUSE_EVENTS_IN  = ['product:audio:pause', 'soundbar:pause', 'videos:pause', 'wsh-fys:audio:pause', 'wsh:audio:play'];

  function fmtTime(sec) {
    if (!isFinite(sec) || sec < 0) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return m + ':' + (s < 10 ? '0' + s : s);
  }

  // Compute real audio duration from an audiowaveform-v2 JSON sidecar.
  // Returns a number of seconds, or null if the sidecar lacks the fields.
  function durationFromPeaksJson(json) {
    if (!json) return null;
    const length = Number(json.length);                 // # of (min,max) pairs
    const spp = Number(json.samples_per_pixel);         // samples per pair
    const sr = Number(json.sample_rate);                // Hz
    if (!(length > 0 && spp > 0 && sr > 0)) return null;
    return (length * spp) / sr;
  }

  function initBanner(root) {
    if (root.__wshFysInit) return;
    root.__wshFysInit = true;

    // Each banner instance tags its outbound pause events with a unique
    // signature so its own inbound listener can skip them (§1a).
    const BANNER_SIG = 'wsh-fys-banner-' + (root.id || '') + '-' + Math.random().toString(36).slice(2, 8);

    const player = root.querySelector('[data-wsh-fys-player]');
    if (!player) return;

    const playBtn    = player.querySelector('[data-wsh-fys-play]');
    const nextBtn    = player.querySelector('[data-wsh-fys-next]');
    const label      = player.querySelector('[data-wsh-fys-player-label]');
    const playLabel  = player.querySelector('[data-wsh-fys-play-label]');
    const timeEl     = player.querySelector('[data-wsh-fys-time]');
    const waveEl     = player.querySelector('[data-wsh-fys-wave]');
    const progressEl = player.querySelector('[data-wsh-fys-progress]');
    const rows       = Array.from(root.querySelectorAll('[data-wsh-fys-row]'));
    if (!rows.length) return;

    const labelNowPlaying = player.getAttribute('data-label-now-playing') || 'NOW PLAYING · {title} · {n} OF 3';
    const labelPlay       = player.getAttribute('data-label-play') || 'PLAY';
    const labelPause      = player.getAttribute('data-label-pause') || 'PAUSE';

    const state = {
      currentIdx: 0,
      isPlaying: false,
      audioEl: null,
      wavesurfer: null,
      wavesurferReady: false,     // true once current ws fires 'ready'
      playOnReady: false,          // set by selectRow / nextTrack / togglePlay to defer .play() until ready
      suppressReenterUntil: 0,
      realDuration: 0              // seconds, from sidecar; 0 → fall back to ws.getDuration()
    };

    function trackAt(idx) {
      const row = rows[idx];
      return {
        row,
        track: row.getAttribute('data-wsh-fys-track') || '',
        title: row.getAttribute('data-wsh-fys-title') || ''
      };
    }

    function currentDuration() {
      if (state.realDuration > 0) return state.realDuration;
      try { return (state.wavesurfer && state.wavesurfer.getDuration()) || 0; } catch (_) { return 0; }
    }

    function updateLabel() {
      const t = trackAt(state.currentIdx);
      if (!t || !label) return;
      label.textContent = labelNowPlaying
        .replace('{title}', t.title)
        .replace('{n}', String(state.currentIdx + 1));
    }

    function highlightCurrent() {
      rows.forEach((r, i) => r.classList.toggle('is-current', i === state.currentIdx));
    }

    function setPlayingClass(flag) {
      root.classList.toggle('wsh-fys-banner--playing', !!flag);
      if (playLabel) playLabel.textContent = flag ? labelPause : labelPlay;
      if (playBtn) playBtn.setAttribute('aria-label', flag ? labelPause : labelPlay);
    }

    function destroyWavesurfer() {
      try { if (state.wavesurfer && state.wavesurfer.destroy) state.wavesurfer.destroy(); } catch (_) {}
      state.wavesurfer = null;
      state.wavesurferReady = false;
      state.realDuration = 0;
    }

    function dispatchOutboundPause() {
      // 60ms suppress window + source-tagged events (§1a).
      state.suppressReenterUntil = Date.now() + 60;
      PAUSE_EVENTS_OUT.forEach(name => {
        try { document.dispatchEvent(new CustomEvent(name, { detail: { source: BANNER_SIG } })); } catch (_) {}
      });
    }

    function pauseLocal() {
      state.isPlaying = false;
      state.playOnReady = false;
      setPlayingClass(false);
      try { if (state.wavesurfer && state.wavesurfer.pause) state.wavesurfer.pause(); } catch (_) {}
      try { if (state.audioEl) state.audioEl.pause(); } catch (_) {}
    }

    function loadTrack(idx) {
      state.currentIdx = ((idx % rows.length) + rows.length) % rows.length;
      updateLabel();
      highlightCurrent();
      const t = trackAt(state.currentIdx);
      destroyWavesurfer();
      if (state.audioEl) { try { state.audioEl.pause(); } catch (_) {} state.audioEl.src = ''; state.audioEl = null; }
      if (progressEl) progressEl.style.width = '0%';
      if (timeEl) timeEl.textContent = '0:00 / 0:00';
      if (!t.track) return;

      if (typeof window.WaveSurfer === 'function') {
        try {
          // Round 3 §1c (Phil 2026-10-14): WaveSurfer v7.3.8 config
          // mirrored from wsh-mixer.js — bars render reliably. The
          // waveEl is EMPTY on create (progressEl moved to a sibling
          // in snippets/wsh-fys-banner.liquid); v7 appends its own
          // wrapper div inside the container and populates the canvas
          // on load — a pre-existing non-WS child breaks that path.
          state.wavesurfer = window.WaveSurfer.create({
            container: waveEl,
            waveColor: 'rgba(240, 232, 213, 0.35)',
            progressColor: '#F0E8D5',
            cursorColor: 'transparent',
            height: 48,
            barWidth: 2,
            barGap: 1,
            barRadius: 1,
            normalize: true,
            interact: true,
            backend: 'MediaElement'
          });
          const ws = state.wavesurfer;
          ws.on('ready', () => {
            if (state.wavesurfer !== ws) return;
            state.wavesurferReady = true;
            // Prefer sidecar duration (set below if peaks arrived); fall
            // back to the real audio metadata duration from WaveSurfer.
            const d = currentDuration() || ws.getDuration() || 0;
            if (timeEl) timeEl.textContent = '0:00 / ' + fmtTime(d);
            if (state.playOnReady) {
              state.playOnReady = false;
              playLocal();
            }
          });
          ws.on('audioprocess', () => {
            if (!state.wavesurfer || state.wavesurfer !== ws) return;
            const d = currentDuration() || ws.getDuration() || 0;
            const c = ws.getCurrentTime();
            if (timeEl) timeEl.textContent = fmtTime(c) + ' / ' + fmtTime(d);
            if (progressEl) progressEl.style.width = (d ? (c / d * 100) : 0) + '%';
          });
          ws.on('finish', () => { nextTrack(/*keepPlaying=*/true); });
          // §1b + §1c — fetch peaks sidecar, compute true duration, load.
          const peaksUrl = t.track.replace(/\.mp3(\?|$)/, '.mp3.json$1');
          fetch(peaksUrl)
            .then(r => r.ok ? r.json() : Promise.reject(new Error('no peaks')))
            .then(json => {
              if (!state.wavesurfer || state.wavesurfer !== ws) return;
              if (!json || !Array.isArray(json.data)) throw new Error('bad peaks');
              const dur = durationFromPeaksJson(json);
              if (dur) state.realDuration = dur;
              // IMPORTANT: WaveSurfer `load(url, peaks, duration)` with a
              // duration arg skips audio decoding and uses it as the
              // playback duration. Pass the SIDECAR duration (true
              // audio seconds), NEVER json.data.length (int count,
              // which was the 1b bug).
              try { ws.load(t.track, json.data, dur || undefined); } catch (_) { ws.load(t.track); }
            })
            .catch(() => {
              if (!state.wavesurfer || state.wavesurfer !== ws) return;
              try { ws.load(t.track); } catch (_) {}
            });
          return;
        } catch (_) {
          destroyWavesurfer();
        }
      }

      // Fallback: raw <audio> + CSS progress bar.
      state.audioEl = new Audio(t.track);
      state.audioEl.preload = 'metadata';
      state.audioEl.addEventListener('loadedmetadata', () => {
        if (timeEl) timeEl.textContent = '0:00 / ' + fmtTime(state.audioEl.duration);
      });
      state.audioEl.addEventListener('timeupdate', () => {
        if (!state.audioEl) return;
        const d = state.audioEl.duration || 0;
        const c = state.audioEl.currentTime || 0;
        if (timeEl) timeEl.textContent = fmtTime(c) + ' / ' + fmtTime(d);
        if (progressEl) progressEl.style.width = (d ? (c / d * 100) : 0) + '%';
      });
      state.audioEl.addEventListener('ended', () => { nextTrack(/*keepPlaying=*/true); });
    }

    function playLocal() {
      const t = trackAt(state.currentIdx);
      if (!t.track) return;
      // §1c — if WaveSurfer isn't ready yet, mark intent and return. The
      // 'ready' handler will call playLocal again when the audio is
      // actually loadable. Prevents the "flicks play/pause, no sound"
      // pattern that happened when `.play()` was called on a WaveSurfer
      // mid-load.
      if (state.wavesurfer && !state.wavesurferReady) {
        state.playOnReady = true;
        state.isPlaying = true;
        setPlayingClass(true);
        return;
      }
      dispatchOutboundPause();
      state.isPlaying = true;
      setPlayingClass(true);
      try {
        if (state.wavesurfer && state.wavesurfer.play) {
          const p = state.wavesurfer.play();
          if (p && p.catch) p.catch(() => {});
        } else if (state.audioEl) {
          const p = state.audioEl.play();
          if (p && p.catch) p.catch(() => {});
        }
      } catch (_) {}
    }

    function togglePlay() {
      if (state.isPlaying) pauseLocal(); else playLocal();
    }

    function nextTrack(forceKeepPlaying) {
      const wasPlaying = (typeof forceKeepPlaying === 'boolean') ? forceKeepPlaying : state.isPlaying;
      pauseLocal();
      loadTrack(state.currentIdx + 1);
      if (wasPlaying) playLocal();   // waits for 'ready' internally per §1c
    }

    function selectRow(idx) {
      // Round 2 §C.2: clicking a row always plays. Round 3 §1c: wait
      // for the new track to be ready before actual .play().
      // Special-case: clicking the already-current playing row pauses.
      if (idx === state.currentIdx && state.isPlaying) { pauseLocal(); return; }
      pauseLocal();
      loadTrack(idx);
      playLocal();   // waits for 'ready' internally per §1c
    }

    // Wire controls
    if (playBtn) playBtn.addEventListener('click', togglePlay);
    if (nextBtn) nextBtn.addEventListener('click', () => nextTrack());
    rows.forEach((r, i) => {
      r.setAttribute('role', 'button');
      r.setAttribute('tabindex', '0');
      r.addEventListener('click', (e) => {
        if (e.target.closest('a, button')) return;
        selectRow(i);
      });
      r.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectRow(i); }
      });
    });

    // Pause-contract listener (§1a): ignore events we dispatched ourselves.
    const onExternalPause = (e) => {
      if (e && e.detail && e.detail.source === BANNER_SIG) return;
      if (Date.now() < state.suppressReenterUntil) return;
      if (!state.isPlaying) return;
      pauseLocal();
    };
    PAUSE_EVENTS_IN.forEach(name => document.addEventListener(name, onExternalPause));

    // Initial state — paint waveform for row 0 immediately (§1c).
    highlightCurrent();
    updateLabel();
    loadTrack(0);

    // §1d — hero "Play Real Sounds" wiring. Links to #find-your-sound
    // (switched from #play-first-demo, which the old wsh-collection-tabs
    // handler also listens to by href). On match: scroll banner into
    // view and play row 0. selectRow(0) handles the "wait for ready"
    // path, so cold-first-click plays reliably.
    if (!root.matches(HOME_SELECTOR)) return;   // CL-5: hero hook is homepage-only
    const TRIGGER_HASHES = new Set(['#find-your-sound', '#' + (root.id || '')]);
    document.addEventListener('click', (e) => {
      const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      const href = a.getAttribute('href') || '';
      const hash = href.startsWith('#') ? href : (href.indexOf('#') >= 0 ? href.slice(href.indexOf('#')) : '');
      if (!hash || !TRIGGER_HASHES.has(hash)) return;
      e.preventDefault();
      // Round 3 §5: hero's "Play Real Sounds" doesn't navigate to the
      // FYS page itself — it scrolls + plays row 0 right here. First-
      // sticky write to sessionStorage so a subsequent banner CTA
      // click (which carries ?src=home-banner) resolves to 'hero'
      // on the FYS page via the first-sticky rule in wsh-fys.js.
      try {
        const sticky = sessionStorage.getItem('wsh_fys_src');
        if (!sticky) sessionStorage.setItem('wsh_fys_src', 'hero');
      } catch (_) {}
      try { root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) {}
      // Scroll settle before play so the user sees the row they're hearing.
      setTimeout(() => selectRow(0), 400);
    });
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.hasAttribute('data-wsh-fys-lazy')) return;
      initBanner(root);
    });
  }

  // Round 3 §1c (Phil 2026-10-14): WaveSurfer loads via the app-extension
  // (wsh-custom-features/wavesurfer.min.js) with `defer`. Our banner JS
  // also loads with `defer`, but in practice the section (and its script
  // tag) may be lazy-rendered by Horizon's view-transitions after
  // DOMContentLoaded. The two defer timings aren't guaranteed; if our
  // script runs BEFORE wavesurfer.min.js, loadTrack skips WaveSurfer and
  // falls through to raw-audio (functional, but no waveform canvas).
  // Poll briefly for window.WaveSurfer, then init. Caps at 2s so a real
  // missing-WaveSurfer environment still falls through to the raw-audio
  // path, no silent hang.
  function whenWaveSurfer(cb) {
    if (typeof window.WaveSurfer === 'function') { cb(); return; }
    const deadline = Date.now() + 2000;
    const tick = () => {
      if (typeof window.WaveSurfer === 'function' || Date.now() > deadline) { cb(); return; }
      setTimeout(tick, 50);
    };
    tick();
  }

  function initWhenReady() { whenWaveSurfer(initAll); }

  // CL-5: lazy roots start on their first activation (idempotent).
  document.addEventListener('wsh-fys-player:activate', (e) => {
    const root = e.target;
    if (!root || !root.matches || !root.matches(SELECTOR)) return;
    whenWaveSurfer(() => initBanner(root));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWhenReady);
  } else {
    initWhenReady();
  }
})();
